import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { IconArrowLeft } from '@tabler/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useInternalSupportCase } from '../../../data/support/useSupport';
import CaseStatusChip from './CaseStatusChip';
import SLAIndicator from './SLAIndicator';
import SupportCaseChat from './SupportCaseChat';
import SupportCaseNotes from './SupportCaseNotes';
import SupportCaseHistory from './SupportCaseHistory';
import SupportCaseSidebar from './SupportCaseSidebar';

export default function InternalSupportCaseDetail() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const caseQuery = useInternalSupportCase(caseId!);
  const [tab, setTab] = React.useState(0);

  if (caseQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (caseQuery.isError || !caseQuery.data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load case details</Alert>
      </Box>
    );
  }

  const supportCase = caseQuery.data;

  return (
    <Box sx={{ p: 3 }}>
      {/* Back link */}
      <Link
        component="button"
        variant="body2"
        onClick={() => navigate('/internal/support/cases')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 2,
          textDecoration: 'none',
        }}
      >
        <IconArrowLeft size={16} />
        Back to Cases
      </Link>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="h5"
            component="span"
            sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
          >
            #{supportCase.case_number}
          </Typography>
          <Typography variant="h5">{supportCase.subject}</Typography>
          <CaseStatusChip value={supportCase.status} type="status" />
          <CaseStatusChip value={supportCase.priority} type="priority" />
          <SLAIndicator
            createdAt={supportCase.created_at}
            status={supportCase.status}
            priority={supportCase.priority}
          />
        </Box>
        {supportCase.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
          >
            {supportCase.description}
          </Typography>
        )}
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left — Tabs */}
        <Box sx={{ flex: 7, minWidth: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Chat" />
              <Tab label="Notes" />
              <Tab label="History" />
            </Tabs>
          </Box>

          {tab === 0 && <SupportCaseChat caseId={caseId!} />}
          {tab === 1 && <SupportCaseNotes caseId={caseId!} />}
          {tab === 2 && <SupportCaseHistory caseId={caseId!} />}
        </Box>

        {/* Right — Sidebar */}
        <Box sx={{ flex: 3, minWidth: 240 }}>
          <SupportCaseSidebar supportCase={supportCase} />
        </Box>
      </Box>
    </Box>
  );
}
